<template>
    <div class="pmt-container">
        <!-- Tabs -->
        <div class="pmt-tabs">
            <button
                :class="['pmt-tab', { active: activeTab === 'account' }]"
                @click="activeTab = 'account'"
            >
                Account Products
                <span class="pmt-tab-badge">{{ accountProducts.length }}</span>
            </button>
            <button
                :class="['pmt-tab', { active: activeTab === 'available' }]"
                @click="activeTab = 'available'"
            >
                All Available Products
                <span class="pmt-tab-badge">{{ productData.length }}</span>
            </button>
        </div>

        <!-- Toolbar -->
        <div class="pmt-toolbar">
            <input
                v-model.trim="searchQuery"
                type="search"
                class="pmt-search"
                placeholder="Search products..."
                aria-label="Search products"
            />
            <select v-model="filterProductCode" class="pmt-filter">
                <option value="">All Product Families</option>
                <option v-for="code in uniqueProductCodes" :key="code" :value="code">
                    {{ code }}
                </option>
            </select>
            <select v-model="filterServiceGroup" class="pmt-filter">
                <option value="">All Service Groups</option>
                <option v-for="group in uniqueServiceGroups" :key="group" :value="group">
                    {{ group }}
                </option>
            </select>
            <label class="pmt-checkbox-label">
                <input type="checkbox" v-model="filterAdditionalOnly" />
                Additional Services Only
            </label>
            <!-- defaultProduct is a master-catalog flag and isn't stored on account product
                 records, so this filter only applies to the All Available Products tab. -->
            <label v-if="activeTab === 'available'" class="pmt-checkbox-label">
                <input type="checkbox" v-model="filterDefaultOnly" />
                Default Products Only
            </label>
        </div>

        <!-- Table -->
        <div class="pmt-table-wrapper">
            <table class="pmt-table">
                <thead>
                    <tr>
                        <th class="pmt-th-check">
                            <input
                                type="checkbox"
                                :checked="allVisibleSelected"
                                :indeterminate.prop="someVisibleSelected && !allVisibleSelected"
                                @change="toggleAllVisible"
                                title="Select all visible"
                            />
                        </th>
                        <th @click="setSort('productName')" class="pmt-th-sortable">
                            Product Name {{ sortIndicator('productName') }}
                        </th>
                        <th @click="setSort('productCode')" class="pmt-th-sortable">
                            Product Code {{ sortIndicator('productCode') }}
                        </th>
                        <th @click="setSort('serviceCode')" class="pmt-th-sortable">
                            Service Code {{ sortIndicator('serviceCode') }}
                        </th>
                        <th @click="setSort('serviceGroup')" class="pmt-th-sortable">
                            Service Group {{ sortIndicator('serviceGroup') }}
                        </th>
                        <th @click="setSort('description')" class="pmt-th-sortable">
                            Description {{ sortIndicator('description') }}
                        </th>
                        <th @click="setSort('price')" class="pmt-th-sortable">
                            Price {{ sortIndicator('price') }}
                        </th>
                        <th @click="setSort('serviceNetwork')" class="pmt-th-sortable">
                            Network {{ sortIndicator('serviceNetwork') }}
                        </th>
                        <th @click="setSort('productSequence')" class="pmt-th-sortable">
                            Seq {{ sortIndicator('productSequence') }}
                        </th>
                        <th>Tier Price</th>
                        <th>Action</th>
                        <th v-if="specialPricing">Custom Price</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(group, groupIndex) in groupedProducts" :key="group.code">
                        <!-- Group header row -->
                        <tr class="pmt-group-header" :class="groupIndex % 2 === 0 ? 'pmt-group-even' : 'pmt-group-odd'">
                            <td :colspan="specialPricing ? 12 : 11" class="pmt-group-cell">
                                <strong>{{ group.name || group.code }}</strong>
                                <span class="pmt-group-code">{{ group.code }}</span>
                                <span class="pmt-group-count">{{ group.products.length }} product{{ group.products.length === 1 ? '' : 's' }}</span>
                            </td>
                        </tr>
                        <!-- Product rows in group -->
                        <template v-for="product in group.products" :key="productKey(product)">
                            <tr
                                :class="[
                                    groupIndex % 2 === 0 ? 'pmt-group-even-row' : 'pmt-group-odd-row',
                                    { 'pmt-row-selected': isSelected(product) }
                                ]"
                            >
                                <td class="pmt-td-check">
                                    <input
                                        type="checkbox"
                                        :checked="isSelected(product)"
                                        @change="toggleProduct(product)"
                                    />
                                </td>
                                <td>{{ product.productName || '—' }}</td>
                                <td class="pmt-td-code">{{ product.productCode || '—' }}</td>
                                <td>{{ product.serviceCode || '—' }}</td>
                                <td>{{ product.serviceGroup || '—' }}</td>
                                <td class="pmt-td-desc">{{ product.description || '—' }}</td>
                                <td>{{ product.price != null ? product.price : '—' }}</td>
                                <td>{{ product.serviceNetwork || '—' }}</td>
                                <td>{{ product.productSequence || '—' }}</td>
                                <td>
                                    <button
                                        v-if="hasTierPrice(product)"
                                        class="pmt-btn pmt-btn-tier"
                                        @click="toggleTierExpand(product)"
                                    >
                                        {{ isTierExpanded(product) ? '▾ Hide' : '▸ Edit' }}
                                    </button>
                                    <span v-else class="pmt-no-tier">—</span>
                                </td>
                                <td>
                                    <button
                                        v-if="!isSelected(product)"
                                        class="pmt-btn pmt-btn-add"
                                        @click="addProduct(product)"
                                    >
                                        Add
                                    </button>
                                    <button
                                        v-else
                                        class="pmt-btn pmt-btn-remove"
                                        @click="removeProduct(product)"
                                    >
                                        Remove
                                    </button>
                                </td>
                                <td v-if="specialPricing">
                                    <input
                                        v-if="isSelected(product)"
                                        type="number"
                                        step="any"
                                        class="pmt-price-input"
                                        :value="getCustomPrice(product)"
                                        @input="setCustomPrice(product, $event.target.value)"
                                    />
                                </td>
                            </tr>
                            <!-- Tier Price expanded row -->
                            <tr v-if="isTierExpanded(product)" class="pmt-tier-row">
                                <td :colspan="specialPricing ? 12 : 11">
                                    <div class="pmt-tier-container">
                                        <h4 class="pmt-tier-title">Tier Pricing — {{ product.productName }}</h4>
                                        <table class="pmt-tier-table">
                                            <thead>
                                                <tr>
                                                    <th>Start</th>
                                                    <th>End</th>
                                                    <th>Price</th>
                                                    <th>Slab</th>
                                                    <th>Allowed</th>
                                                    <th>Remove</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(tier, tierIdx) in getProductTierPrice(product)" :key="tierIdx">
                                                    <td>
                                                        <input
                                                            type="number"
                                                            class="pmt-tier-input"
                                                            :value="tier.start"
                                                            @input="updateTier(product, tierIdx, 'start', $event.target.value)"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            class="pmt-tier-input"
                                                            :value="tier.end"
                                                            @input="updateTier(product, tierIdx, 'end', $event.target.value)"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            class="pmt-tier-input"
                                                            :value="tier.price"
                                                            @input="updateTier(product, tierIdx, 'price', $event.target.value)"
                                                        />
                                                    </td>
                                                    <td class="pmt-tier-center">
                                                        <input
                                                            type="checkbox"
                                                            :checked="tier.slab === true"
                                                            @change="updateTier(product, tierIdx, 'slab', $event.target.checked)"
                                                        />
                                                    </td>
                                                    <td class="pmt-tier-center">
                                                        <input
                                                            type="checkbox"
                                                            :checked="tier.allowed !== false"
                                                            @change="updateTier(product, tierIdx, 'allowed', $event.target.checked)"
                                                        />
                                                    </td>
                                                    <td class="pmt-tier-center">
                                                        <button class="pmt-btn pmt-btn-remove" @click="removeTier(product, tierIdx)">×</button>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <button class="pmt-btn pmt-btn-add-tier" @click="addTier(product)">+ Add Tier</button>
                                        <p v-if="tierPriceError(product)" class="pmt-tier-error">{{ tierPriceError(product) }}</p>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </template>
                    <tr v-if="!groupedProducts.length">
                        <td :colspan="specialPricing ? 12 : 11" class="pmt-empty">
                            No products match your filters.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div class="pmt-pagination">
            <span class="pmt-pagination-info">
                {{ paginationStart }}–{{ paginationEnd }} of {{ filteredProducts.length }}
            </span>
            <button class="pmt-btn pmt-btn-page" :disabled="currentPage === 1" @click="currentPage--">‹ Prev</button>
            <button class="pmt-btn pmt-btn-page" :disabled="currentPage >= totalPages" @click="currentPage++">Next ›</button>
            <select v-model.number="pageSize" class="pmt-page-size">
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
                <option :value="999999">All</option>
            </select>
        </div>
    </div>
</template>

<script>
import { API } from "aws-amplify";

export default {
    name: "ProductManagementTable",

    props: {
        updateAccountProducts: { type: Boolean, default: false },
        currentAccountProducts: { type: Array, default: () => [] },
        specialPricing: { type: Boolean, default: false },
        selectedAccount: { type: String, default: "" },
    },

    emits: ["getSelectedProducts"],

    data() {
        return {
            productData: [],
            selectedProducts: [],
            loadingProducts: false,
            expandedTiers: {},

            activeTab: "account",
            searchQuery: "",
            filterProductCode: "",
            filterServiceGroup: "",
            filterAdditionalOnly: false,
            filterDefaultOnly: false,

            sortField: "productCode",
            sortDirection: "asc",

            currentPage: 1,
            pageSize: 50,
        };
    },

    computed: {
        accountProducts() {
            return this.selectedProducts;
        },

        sourceProducts() {
            return this.activeTab === "account" ? this.selectedProducts : this.productData;
        },

        uniqueProductCodes() {
            const families = new Set();
            this.productData.forEach((p) => { if (p.productFamily) families.add(p.productFamily); });
            return Array.from(families).sort();
        },

        uniqueServiceGroups() {
            const groups = new Set();
            this.productData.forEach((p) => { if (p.serviceGroup) groups.add(p.serviceGroup); });
            return Array.from(groups).sort();
        },

        filteredProducts() {
            let list = this.sourceProducts;

            if (this.searchQuery) {
                const q = this.searchQuery.toLowerCase();
                list = list.filter((p) =>
                    (p.productName || "").toLowerCase().includes(q) ||
                    (p.productCode || "").toLowerCase().includes(q) ||
                    (p.serviceCode || "").toLowerCase().includes(q) ||
                    (p.description || "").toLowerCase().includes(q) ||
                    (p.serviceGroup || "").toLowerCase().includes(q) ||
                    (p.serviceNetwork || "").toLowerCase().includes(q)
                );
            }

            if (this.filterProductCode) {
                list = list.filter((p) => p.productFamily === this.filterProductCode);
            }
            if (this.filterServiceGroup) {
                list = list.filter((p) => p.serviceGroup === this.filterServiceGroup);
            }
            if (this.filterAdditionalOnly) {
                list = list.filter((p) => p.additionalService === true);
            }
            if (this.filterDefaultOnly) {
                list = list.filter((p) => p.defaultProduct === true);
            }

            // Primary sort: always group by productCode
            // Secondary sort: user-chosen sort field within each group
            const field = this.sortField;
            const dir = this.sortDirection === "asc" ? 1 : -1;

            list = [...list].sort((a, b) => {
                // First sort by productCode to keep groups together
                const codeCompare = (a.productCode || "").localeCompare(b.productCode || "");
                if (codeCompare !== 0) return codeCompare;

                // Then sort within the group by the user's chosen field
                let valA = a[field];
                let valB = b[field];
                if (valA == null) valA = "";
                if (valB == null) valB = "";
                if (typeof valA === "number" && typeof valB === "number") return (valA - valB) * dir;
                return String(valA).localeCompare(String(valB)) * dir;
            });

            return list;
        },

        totalPages() {
            return Math.max(1, Math.ceil(this.filteredProducts.length / this.pageSize));
        },

        paginatedProducts() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.filteredProducts.slice(start, start + this.pageSize);
        },

        paginationStart() {
            return this.filteredProducts.length ? (this.currentPage - 1) * this.pageSize + 1 : 0;
        },

        paginationEnd() {
            return Math.min(this.currentPage * this.pageSize, this.filteredProducts.length);
        },

        allVisibleSelected() {
            if (!this.paginatedProducts.length) return false;
            return this.paginatedProducts.every((p) => this.isSelected(p));
        },

        someVisibleSelected() {
            return this.paginatedProducts.some((p) => this.isSelected(p));
        },

        // Group paginated products by productCode for visual clustering
        groupedProducts() {
            const groups = [];
            let currentCode = null;
            let currentGroup = null;

            for (const product of this.paginatedProducts) {
                if (product.productCode !== currentCode) {
                    currentCode = product.productCode;
                    currentGroup = {
                        code: product.productCode || "Unknown",
                        name: this.getGroupName(product.productCode),
                        products: [],
                    };
                    groups.push(currentGroup);
                }
                currentGroup.products.push(product);
            }

            return groups;
        },
    },

    watch: {
        selectedProducts: {
            deep: true,
            handler() { this.$emit("getSelectedProducts", this.selectedProducts); },
        },
        searchQuery() { this.currentPage = 1; },
        filterProductCode() { this.currentPage = 1; },
        filterServiceGroup() { this.currentPage = 1; },
        filterAdditionalOnly() { this.currentPage = 1; },
        filterDefaultOnly() { this.currentPage = 1; },
        activeTab(tab) {
            this.currentPage = 1;
            // The Default Products filter only exists on the available tab; clear it when
            // leaving so a stale filter doesn't hide every account product.
            if (tab !== "available") this.filterDefaultOnly = false;
        },
        productData() { if (this.updateAccountProducts) this.setOverlapProducts(); },
        specialPricing() { if (this.specialPricing) this.getAccountProductsSpecialPricing(); },
    },

    mounted() {
        this.listProductOptions();
        if (this.specialPricing) this.getAccountProductsSpecialPricing();
    },

    methods: {
        productKey(product) {
            return `${product.productCode}-${product.serviceCode}`;
        },

        getGroupName(productCode) {
            // Find the non-additional-service product with the lowest productSequence
            const candidates = this.sourceProducts.filter(
                (p) => p.productCode === productCode && p.additionalService !== true
            );

            if (!candidates.length) {
                // Fallback: any product in this code
                const any = this.sourceProducts.find((p) => p.productCode === productCode);
                return any ? (any.productName || productCode) : productCode;
            }

            candidates.sort((a, b) => {
                const seqA = parseInt(a.productSequence) || 999999;
                const seqB = parseInt(b.productSequence) || 999999;
                return seqA - seqB;
            });

            return candidates[0].productName || productCode;
        },

        isSelected(product) {
            return this.selectedProducts.some(
                (p) => p.productCode === product.productCode && p.serviceCode === product.serviceCode
            );
        },

        addProduct(product) {
            if (!this.isSelected(product)) {
                this.selectedProducts.push({ ...product });
            }
        },

        removeProduct(product) {
            this.selectedProducts = this.selectedProducts.filter(
                (p) => !(p.productCode === product.productCode && p.serviceCode === product.serviceCode)
            );
        },

        toggleProduct(product) {
            if (this.isSelected(product)) this.removeProduct(product);
            else this.addProduct(product);
        },

        toggleAllVisible() {
            if (this.allVisibleSelected) {
                const visibleKeys = new Set(this.paginatedProducts.map((p) => this.productKey(p)));
                this.selectedProducts = this.selectedProducts.filter((p) => !visibleKeys.has(this.productKey(p)));
            } else {
                this.paginatedProducts.forEach((p) => { if (!this.isSelected(p)) this.selectedProducts.push({ ...p }); });
            }
        },

        getCustomPrice(product) {
            const selected = this.selectedProducts.find(
                (p) => p.productCode === product.productCode && p.serviceCode === product.serviceCode
            );
            return selected ? selected.price : product.price;
        },

        setCustomPrice(product, value) {
            const selected = this.selectedProducts.find(
                (p) => p.productCode === product.productCode && p.serviceCode === product.serviceCode
            );
            if (selected) selected.price = value === "" ? null : parseFloat(value);
        },

        setSort(field) {
            if (this.sortField === field) {
                this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
            } else {
                this.sortField = field;
                this.sortDirection = "asc";
            }
        },

        sortIndicator(field) {
            if (this.sortField !== field) return "";
            return this.sortDirection === "asc" ? "▲" : "▼";
        },

        // Tier price methods
        hasTierPrice(product) {
            return Array.isArray(product.tierPrice) && product.tierPrice.length > 0;
        },

        isTierExpanded(product) {
            return !!this.expandedTiers[this.productKey(product)];
        },

        toggleTierExpand(product) {
            const key = this.productKey(product);
            this.expandedTiers = { ...this.expandedTiers, [key]: !this.expandedTiers[key] };
        },

        getProductTierPrice(product) {
            // Use selectedProducts version if selected, otherwise source
            const selected = this.selectedProducts.find(
                (p) => p.productCode === product.productCode && p.serviceCode === product.serviceCode
            );
            const target = selected || product;
            if (!Array.isArray(target.tierPrice)) target.tierPrice = [];
            return target.tierPrice;
        },

        updateTier(product, tierIdx, field, value) {
            const selected = this.selectedProducts.find(
                (p) => p.productCode === product.productCode && p.serviceCode === product.serviceCode
            );
            const target = selected || product;
            if (!Array.isArray(target.tierPrice)) return;

            const tier = target.tierPrice[tierIdx];
            if (!tier) return;

            if (field === "slab" || field === "allowed") {
                tier[field] = value;
            } else {
                tier[field] = value === "" ? null : parseFloat(value);
            }
        },

        removeTier(product, tierIdx) {
            const selected = this.selectedProducts.find(
                (p) => p.productCode === product.productCode && p.serviceCode === product.serviceCode
            );
            const target = selected || product;
            if (Array.isArray(target.tierPrice)) {
                target.tierPrice.splice(tierIdx, 1);
            }
        },

        addTier(product) {
            const selected = this.selectedProducts.find(
                (p) => p.productCode === product.productCode && p.serviceCode === product.serviceCode
            );
            const target = selected || product;
            if (!Array.isArray(target.tierPrice)) target.tierPrice = [];
            target.tierPrice.push({ start: 0, end: 0, price: 0, slab: null, allowed: true });
        },

        // Validate a single product's tier ranges. Returns an error string or null.
        // Prices may be negative, so only start/end ranges are validated.
        tierPriceError(product) {
            const tiers = Array.isArray(product.tierPrice) ? product.tierPrice : [];
            if (tiers.length === 0) return null;

            for (let i = 0; i < tiers.length; i++) {
                const start = Number(tiers[i].start);
                const end = Number(tiers[i].end);
                if (Number.isNaN(start) || Number.isNaN(end)) {
                    return `Tier ${i + 1} must have a numeric start and end.`;
                }
                if (start > end) {
                    return `Tier ${i + 1} start (${start}) cannot be greater than its end (${end}).`;
                }
            }

            // Ranges are inclusive on both ends, so touching endpoints count as overlap.
            const sorted = [...tiers].sort((a, b) => Number(a.start) - Number(b.start));
            for (let i = 1; i < sorted.length; i++) {
                if (Number(sorted[i].start) <= Number(sorted[i - 1].end)) {
                    return `Tier ranges overlap: ${sorted[i - 1].start}-${sorted[i - 1].end} and ${sorted[i].start}-${sorted[i].end}.`;
                }
            }
            return null;
        },

        // Validate all selected products' tiers. Returns an error message or null.
        // The parent can call this via a ref before saving account products.
        validateAllTiers() {
            for (const product of this.selectedProducts) {
                const err = this.tierPriceError(product);
                if (err) {
                    return `${product.productCode}/${product.serviceCode}: ${err}`;
                }
            }
            return null;
        },

        async listProductOptions() {
            this.loadingProducts = true;
            try {
                const response = await API.graphql({
                    query: `query listProducts {
                        listProducts {
                            items {
                                productCode
                                serviceCode
                                description
                                productSequence
                                serviceNetwork
                                additionalService
                                defaultProduct
                                price
                                productName
                                vendor
                                restServiceCode
                                descriptionUI
                                deliveryWindowEnd
                                deliveryWindowStart
                                deliveryWindowLabel
                                productFamily
                                serviceGroup
                                serviceGroupSequence
                                fuelSurchargeCode
                                quantity
                                DiscountType
                                discountAmount
                                tierPrice {
                                    start
                                    end
                                    price
                                    slab
                                    allowed
                                }
                            }
                        }
                    }`,
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                });
                this.productData = response.data.listProducts.items || [];
            } catch (error) {
                console.error("Error loading products:", error);
            } finally {
                this.loadingProducts = false;
            }
        },

        async getAccountProductsSpecialPricing() {
            this.loadingProducts = true;
            try {
                const response = await API.graphql({
                    query: `query getAccount {
                        getAccount(accountNo: "${this.selectedAccount}") {
                            product {
                                productCode
                                serviceCode
                                description
                                productSequence
                                serviceNetwork
                                additionalService
                                defaultProduct
                                price
                                productName
                                vendor
                                restServiceCode
                                descriptionUI
                                deliveryWindowEnd
                                deliveryWindowStart
                                deliveryWindowLabel
                                productFamily
                                serviceGroup
                                serviceGroupSequence
                                fuelSurchargeCode
                                quantity
                                DiscountType
                                discountAmount
                                tierPrice {
                                    start
                                    end
                                    price
                                    slab
                                    allowed
                                }
                            }
                        }
                    }`,
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                });
                this.selectedProducts = response.data.getAccount.product || [];
            } catch (error) {
                console.error("Error loading account products:", error);
            } finally {
                this.loadingProducts = false;
            }
        },

        setOverlapProducts() {
            const accountKeys = new Set(
                this.currentAccountProducts.map((p) => `${p.productCode}-${p.serviceCode}`)
            );
            this.selectedProducts = this.currentAccountProducts.filter((p) =>
                accountKeys.has(`${p.productCode}-${p.serviceCode}`)
            );
        },
    },
};
</script>

<style scoped>
.pmt-container {
    width: 100%;
    max-width: 100%;
    font-family: inherit;
    font-size: 13px;
    border: 1px solid #dfe3e8;
    border-radius: 12px;
    padding: 14px;
    box-sizing: border-box;
    overflow: hidden;
}

/* Tabs */
.pmt-tabs {
    display: flex;
    gap: 0;
    border-bottom: 2px solid #e0e0e0;
    margin-bottom: 12px;
}

.pmt-tab {
    padding: 8px 16px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: #555;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    transition: 0.2s;
}

.pmt-tab:hover {
    color: #222;
    background-color: #f5f5f5;
}

.pmt-tab.active {
    color: #1f5f99;
    border-bottom-color: #1f5f99;
}

.pmt-tab-badge {
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 10px;
    background-color: #eef1f4;
    font-size: 11px;
    font-weight: 700;
}

.pmt-tab.active .pmt-tab-badge {
    background-color: #dbeaf5;
    color: #1f5f99;
}

/* Toolbar */
.pmt-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-bottom: 10px;
}

.pmt-search {
    flex: 1;
    min-width: 180px;
    padding: 6px 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    font-size: 12px;
}

.pmt-search:focus {
    border-color: #1f5f99;
    outline: none;
    box-shadow: 0 0 0 2px rgba(31, 95, 153, 0.1);
}

.pmt-filter {
    padding: 6px 8px;
    border: 1px solid #ccc;
    border-radius: 5px;
    font-size: 12px;
    background: #fff;
}

.pmt-checkbox-label {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
}

/* Table */
.pmt-table-wrapper {
    overflow-x: auto;
    border: 1px solid #dfe3e8;
    border-radius: 6px;
    max-height: 70vh;
    overflow-y: auto;
}

.pmt-table {
    width: 100%;
    border-collapse: collapse;
    white-space: nowrap;
}

.pmt-table thead {
    background-color: #f5f7f9;
    position: sticky;
    top: 0;
    z-index: 2;
}

.pmt-table th {
    padding: 8px 10px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #444;
    border-bottom: 2px solid #dfe3e8;
    user-select: none;
}

.pmt-th-sortable {
    cursor: pointer;
}

.pmt-th-sortable:hover {
    background-color: #eaecef;
}

.pmt-th-check {
    width: 32px;
    text-align: center;
}

.pmt-table td {
    padding: 6px 10px;
    border-bottom: 1px solid #eef1f4;
    color: #333;
}

.pmt-td-check {
    text-align: center;
}

.pmt-td-code {
    font-family: monospace;
    font-size: 11px;
}

.pmt-td-desc {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Group headers */
.pmt-group-header td {
    padding: 6px 12px;
    font-size: 12px;
    border-bottom: 1px solid #d0d5db;
}

.pmt-group-even .pmt-group-cell {
    background-color: #f0f4f8;
    border-left: 4px solid #1f5f99;
}

.pmt-group-odd .pmt-group-cell {
    background-color: #f8f5f0;
    border-left: 4px solid #d4860a;
}

.pmt-group-code {
    margin-left: 8px;
    padding: 1px 6px;
    border-radius: 3px;
    background-color: rgba(0, 0, 0, 0.06);
    font-size: 10px;
    font-family: monospace;
    color: #555;
}

.pmt-group-count {
    margin-left: 8px;
    color: #888;
    font-size: 11px;
    font-weight: 400;
}

/* Alternating group row backgrounds */
.pmt-group-even-row {
    background-color: #fafcfe;
}

.pmt-group-odd-row {
    background-color: #fefcfa;
}

.pmt-group-even-row:hover,
.pmt-group-odd-row:hover {
    background-color: #f0f3f6 !important;
}

.pmt-row-selected {
    background-color: #e6f7e9 !important;
}

.pmt-empty {
    text-align: center;
    padding: 24px !important;
    color: #888;
    font-style: italic;
}

/* Buttons */
.pmt-btn {
    padding: 3px 8px;
    border: none;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: 0.15s;
}

.pmt-btn-add {
    background-color: #33f18a;
    color: #fff;
}

.pmt-btn-add:hover {
    background-color: #28d877;
}

.pmt-btn-remove {
    background-color: #fe804d;
    color: #fff;
}

.pmt-btn-remove:hover {
    background-color: #e56d3e;
}

.pmt-btn-tier {
    background-color: #eef3f8;
    color: #1f5f99;
    border: 1px solid #c8d8e8;
}

.pmt-btn-tier:hover {
    background-color: #dbeaf5;
}

.pmt-btn-add-tier {
    margin-top: 6px;
    background-color: #1f5f99;
    color: #fff;
    padding: 4px 10px;
}

.pmt-btn-add-tier:hover {
    background-color: #174a7a;
}

.pmt-tier-error {
    margin: 8px 0 0 0;
    font-size: 12px;
    color: #c0392b;
    font-weight: bold;
}

.pmt-btn-page {
    background-color: #f0f0f0;
    color: #333;
    padding: 4px 10px;
}

.pmt-btn-page:hover:not(:disabled) {
    background-color: #e0e0e0;
}

.pmt-btn-page:disabled {
    opacity: 0.4;
    cursor: default;
}

.pmt-no-tier {
    color: #bbb;
    font-size: 11px;
}

/* Price input */
.pmt-price-input {
    width: 70px;
    padding: 3px 6px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 12px;
}

.pmt-price-input:focus {
    border-color: #1f5f99;
    outline: none;
}

/* Tier Price expanded row */
.pmt-tier-row td {
    padding: 0;
    background-color: #f8fafd;
    border-bottom: 2px solid #d0d5db;
}

.pmt-tier-container {
    padding: 10px 16px 12px 40px;
}

.pmt-tier-title {
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 700;
    color: #1f5f99;
}

.pmt-tier-table {
    width: auto;
    border-collapse: collapse;
    font-size: 12px;
}

.pmt-tier-table th {
    padding: 4px 10px;
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    color: #555;
    border-bottom: 1px solid #ddd;
}

.pmt-tier-table td {
    padding: 4px 10px;
    border-bottom: 1px solid #eee;
}

.pmt-tier-input {
    width: 65px;
    padding: 3px 5px;
    border: 1px solid #ccc;
    border-radius: 3px;
    font-size: 12px;
}

.pmt-tier-input:focus {
    border-color: #1f5f99;
    outline: none;
}

.pmt-tier-center {
    text-align: center;
}

/* Pagination */
.pmt-pagination {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
    font-size: 12px;
}

.pmt-pagination-info {
    color: #666;
}

.pmt-page-size {
    margin-left: auto;
    padding: 3px 6px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 12px;
}
</style>
